import api from "@/lib/api";

export interface OIDCConfig {
  enabled: boolean;
  corporateIdentityRequired: boolean;
  issuer: string;
  clientId: string;
  authorizationEndpoint: string;
  tokenEndpoint: string;
  logoutEndpoint: string;
  redirectUri: string;
  acrValues: string[];
  mfaMaxAgeSeconds: number;
}

export interface OIDCTokenResponse {
  access: string;
  refresh?: string;
  id_token?: string;
}

interface KeycloakLoginOptions {
  prompt?: "login";
  maxAge?: number;
}

const LOGOUT_IN_PROGRESS_KEY = "sentinels_logout_in_progress";
const LOGGED_OUT_KEY = "sentinels_logged_out";
const LOGGED_OUT_PATH = "/logged-out";
const MAX_CORPORATE_MFA_AGE_SECONDS = 300;

export function buildOIDCLogoutUrl(
  config: Pick<OIDCConfig, "clientId" | "logoutEndpoint">,
  origin = window.location.origin,
  idToken?: string | null,
): string {
  const returnToLoggedOut = `${origin}${LOGGED_OUT_PATH}`;
  const params = new URLSearchParams({
    client_id: config.clientId,
    post_logout_redirect_uri: returnToLoggedOut,
    redirect_uri: returnToLoggedOut,
  });
  if (idToken) {
    params.set("id_token_hint", idToken);
  }
  return `${config.logoutEndpoint}?${params.toString()}`;
}

export function buildOIDCLoginUrl(
  config: Pick<
    OIDCConfig,
    "authorizationEndpoint" | "clientId" | "redirectUri" | "acrValues"
  >,
  state: string,
  nonce: string,
  codeChallenge: string,
  options: KeycloakLoginOptions = {},
): string {
  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: "code",
    scope: "openid profile email",
    state,
    nonce,
  });
  // PKCE is optional for confidential clients. The server-side callback
  // (Django GET handler) cannot access the browser's sessionStorage verifier,
  // so we skip code_challenge entirely. The client secret authenticates the
  // token exchange server-side.
  if (codeChallenge) {
    params.set("code_challenge", codeChallenge);
    params.set("code_challenge_method", "S256");
  }
  if (options.prompt) {
    params.set("prompt", options.prompt);
  }
  if (options.maxAge !== undefined) {
    if (
      !Number.isSafeInteger(options.maxAge) ||
      options.maxAge < 1 ||
      options.maxAge > MAX_CORPORATE_MFA_AGE_SECONDS
    ) {
      throw new Error("Corporate MFA max_age is not configured safely.");
    }
    params.set("max_age", String(options.maxAge));
  }
  if (config.acrValues.length > 0) {
    if (!config.acrValues.every((value) => value === "2")) {
      throw new Error("Corporate MFA assurance is not configured safely.");
    }
    params.set("acr_values", config.acrValues.join(" "));
  }
  return `${config.authorizationEndpoint}?${params.toString()}`;
}

function base64UrlEncode(bytes: ArrayBuffer): string {
  const binary = String.fromCharCode(...new Uint8Array(bytes));
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function randomBase64Url(byteLength = 64): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes.buffer);
}

export async function pkceChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(digest);
}

export function getOIDCConfig(): OIDCConfig {
  const enabled = import.meta.env.VITE_OIDC_ENABLED === "true";
  const corporateIdentityRequired =
    import.meta.env.SENTINELS_CORPORATE_IDENTITY_REQUIRED === "true";
  const issuer = import.meta.env.VITE_OIDC_ISSUER ?? "";
  const configuredAcrValuesRaw: string =
    import.meta.env.SENTINELS_CORPORATE_MFA_ACR_VALUES ?? "";
  const configuredAcrValues = configuredAcrValuesRaw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  const acrValues = configuredAcrValues.every((value) => value === "2")
    ? configuredAcrValues
    : [];
  const configuredMaxAge: string =
    import.meta.env.SENTINELS_CORPORATE_MFA_MAX_AGE_SECONDS ?? "300";
  const parsedMaxAge = Number(configuredMaxAge.trim());
  return {
    enabled,
    corporateIdentityRequired,
    issuer,
    clientId: import.meta.env.VITE_OIDC_CLIENT_ID ?? "sentinels-core",
    authorizationEndpoint:
      import.meta.env.VITE_OIDC_AUTHORIZATION_ENDPOINT ??
      `${issuer}/protocol/openid-connect/auth`,
    tokenEndpoint:
      import.meta.env.VITE_OIDC_TOKEN_ENDPOINT ??
      `${issuer}/protocol/openid-connect/token`,
    logoutEndpoint:
      import.meta.env.VITE_OIDC_LOGOUT_ENDPOINT ??
      `${issuer}/protocol/openid-connect/logout`,
    redirectUri: `${window.location.origin}/auth/oidc/callback`,
    acrValues,
    mfaMaxAgeSeconds:
      Number.isSafeInteger(parsedMaxAge) &&
      parsedMaxAge > 0 &&
      parsedMaxAge <= MAX_CORPORATE_MFA_AGE_SECONDS
        ? parsedMaxAge
        : 0,
  };
}

export async function redirectToKeycloakLogin(
  orgId?: string,
  options: KeycloakLoginOptions = {},
): Promise<void> {
  const config = getOIDCConfig();
  if (!config.enabled || !config.authorizationEndpoint) return;
  if (
    config.corporateIdentityRequired &&
    (config.acrValues.length === 0 || config.mfaMaxAgeSeconds < 1)
  ) {
    throw new Error("Corporate MFA assurance is not configured safely.");
  }

  localStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
  localStorage.removeItem(LOGGED_OUT_KEY);

  const state = crypto.randomUUID();
  const nonce = crypto.randomUUID();
  const codeVerifier = randomBase64Url();
  const codeChallenge = ""; // skip PKCE — server-side exchange uses client secret
  sessionStorage.setItem("oidc_state", state);
  sessionStorage.setItem("oidc_nonce", nonce);
  sessionStorage.setItem("oidc_code_verifier", codeVerifier);
  if (orgId) sessionStorage.setItem("oidc_org_id", orgId);

  window.location.href = buildOIDCLoginUrl(
    config.corporateIdentityRequired ? config : { ...config, acrValues: [] },
    state,
    nonce,
    codeChallenge,
    options.prompt
      ? { prompt: "login", maxAge: config.mfaMaxAgeSeconds }
      : options,
  );
}

export function redirectToKeycloakLogout(): boolean {
  const config = getOIDCConfig();
  if (!config.enabled || !config.logoutEndpoint) return false;

  localStorage.setItem(LOGOUT_IN_PROGRESS_KEY, "true");
  localStorage.setItem(LOGGED_OUT_KEY, "true");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("active_organization_id");
  localStorage.removeItem("active_workspace_id");

  sessionStorage.removeItem("oidc_state");
  sessionStorage.removeItem("oidc_nonce");
  sessionStorage.removeItem("oidc_code_verifier");
  sessionStorage.removeItem("oidc_org_id");

  const idToken =
    import.meta.env.VITE_OIDC_ENABLED === "true"
      ? null
      : (localStorage.getItem("oidc_id_token") ??
        sessionStorage.getItem("oidc_id_token"));
  localStorage.removeItem("oidc_id_token");
  sessionStorage.removeItem("oidc_id_token");
  window.location.href = buildOIDCLogoutUrl(
    config,
    window.location.origin,
    idToken,
  );
  return true;
}

export function isLogoutInProgress(): boolean {
  return localStorage.getItem(LOGOUT_IN_PROGRESS_KEY) === "true";
}

export function markLoggedOut(): void {
  localStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
  localStorage.setItem(LOGGED_OUT_KEY, "true");
}

export function hasManualLogout(): boolean {
  return localStorage.getItem(LOGGED_OUT_KEY) === "true";
}

export async function exchangeCodeForToken(
  code: string,
): Promise<OIDCTokenResponse> {
  const config = getOIDCConfig();
  const orgId = sessionStorage.getItem("oidc_org_id") ?? undefined;
  const codeVerifier =
    sessionStorage.getItem("oidc_code_verifier") ?? undefined;
  const { data } = await api.post<
    { data: OIDCTokenResponse } | OIDCTokenResponse
  >("/auth/oidc/callback/", {
    code,
    redirect_uri: config.redirectUri,
    org_id: orgId,
    code_verifier: codeVerifier,
  });
  return "data" in data && "status" in (data as Record<string, unknown>)
    ? (data as { data: OIDCTokenResponse }).data
    : (data as OIDCTokenResponse);
}
